import { useMemo, useRef, useEffect, useState, forwardRef } from "react";
import HTMLFlipBook from "react-pageflip";
import BookPageEn from "./BookPageEn";

/**
 * BookReader3DEn — English-mode 3D page-flip book.
 *
 * Pair of `BookReader3D.jsx` (Hebrew, untouched).
 *
 * LTR STRATEGY — natural library flow, no reversal:
 *   ────────────────────────────────────────────────
 *   react-pageflip is hard-wired LTR, which matches English perfectly.
 *   • `pages` (semantic order) === renderedPages (natural order).
 *   • `startPage = 0` — book opens on Genesis 1:1 on the LEFT page.
 *   • Swipe LEFT (dx < 0) advances → flipNext.
 *   • Swipe RIGHT  retreats        → flipPrev.
 *   • `flipForward()` === flipNext, `flipBackward()` === flipPrev.
 *
 * Pagination uses English character counts (typically 1.6–1.8× the
 * Hebrew length per verse), so fewer verses fit per page. Defaults
 * tuned empirically against Koren Tanakh + Steinsaltz Talmud.
 *
 * IMPORTANT: This component is intentionally a parallel implementation
 * of `BookReader3D`; do NOT cross-couple them. The Hebrew flow must
 * never depend on this file.
 */
const BookReader3DEn = forwardRef(function BookReader3DEn(
  {
    verses = [],
    bookHe,             // received but only used for the floating pill (parent owns that)
    bookEn,
    chapter,
    totalChapters,
    onVerseClick,
    activeVerseNum,
    highlights = {},
    notesByVerse = {},
    showVerseNums = true,
    onPageFlip,
    tocPages = [],
    onPrevChapter,
    onNextChapter,
    hasPrev = false,
    hasNext = false,
    autoContinue = false,
    nextChapterLabelEn = "Continue to next chapter",
    chapterLabelEnFn,
  },
  ref
) {
  const bookRef = useRef(null);
  const stageRef = useRef(null);
  const pagingRef = useRef({ verses, tocPages, hasPrev, pages: [] });
  const [stageWidth, setStageWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [dims, setDims] = useState(() =>
    computeDims(
      typeof window !== "undefined" ? window.innerWidth : 1200,
      typeof window !== "undefined" ? window.innerHeight : 900
    )
  );

  useEffect(() => {
    const el = stageRef.current;
    const recompute = () => {
      const w = el?.getBoundingClientRect?.()?.width || window.innerWidth;
      setStageWidth(Math.round(w));
      setDims(computeDims(w, window.innerHeight));
    };
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", recompute);
      window.addEventListener("orientationchange", recompute);
      return () => {
        window.removeEventListener("resize", recompute);
        window.removeEventListener("orientationchange", recompute);
      };
    }
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width || window.innerWidth);
      setStageWidth(w);
    });
    ro.observe(el);
    window.addEventListener("orientationchange", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", recompute);
    };
  }, []);

  useEffect(() => {
    setDims(computeDims(stageWidth, window.innerHeight));
  }, [stageWidth]);

  // LTR flip helpers — forward maps directly to library's flipNext.
  useEffect(() => {
    if (!ref) return;
    const safeFlip = (which) => {
      const flip = bookRef.current?.pageFlip?.();
      if (!flip) return;
      const settings = flip.getSettings?.();
      let prev;
      if (settings) {
        prev = settings.disableFlipByClick;
        settings.disableFlipByClick = false;
      }
      try {
        if (which === "prev") flip.flipPrev();
        else flip.flipNext();
      } finally {
        if (settings) {
          setTimeout(() => {
            settings.disableFlipByClick = prev;
          }, 60);
        }
      }
    };
    const api = {
      flipForward: () => safeFlip("next"),  // LTR: forward = library next
      flipBackward: () => safeFlip("prev"), // LTR: backward = library prev
      flipNext: () => safeFlip("next"),
      flipPrev: () => safeFlip("prev"),
      goTo: (n) => bookRef.current?.pageFlip?.()?.flip(n),
      goToVerse: (verseNum) => {
        if (!verseNum || !bookRef.current?.pageFlip) return false;
        const { verses: vs, tocPages: tp0, pages: ps } = pagingRef.current || {};
        if (!vs || vs.length === 0 || !ps || ps.length === 0) return false;
        const tp = buildTextPagesEn({
          verses: vs,
          maxCharsPerPage: 1300,
          maxVersesPerPage: 6,
        });
        const textIdx = tp.findIndex((p) =>
          p.variant === "text" && p.verses?.some?.((v) => v.verse === verseNum)
        );
        if (textIdx < 0) return false;
        const semanticIndex = (tp0?.length || 0) + textIdx;
        // LTR: no reversal. Snap to even page index so target lands on
        // the left page of the spread (the natural reading entry).
        const snappedIndex = semanticIndex - (semanticIndex % 2);
        const pf = bookRef.current.pageFlip();
        if (!pf || typeof pf.flip !== "function") return false;
        try {
          pf.flip(snappedIndex);
        } catch {
          return false;
        }
        return true;
      },
    };
    if (typeof ref === "function") ref(api);
    else ref.current = api;
  }, [ref]);

  const textPages = useMemo(
    () =>
      buildTextPagesEn({
        verses,
        // English Koren averages ~1.7× Hebrew char count. The Hebrew
        // pager uses 820/10 — these defaults give a roughly equivalent
        // reading rhythm in English without overflow.
        maxCharsPerPage: 1300,
        maxVersesPerPage: 6,
      }),
    [verses]
  );

  const pages = useMemo(() => {
    const combined = [...tocPages, ...textPages];
    const tail = hasNext
      ? [
          {
            variant: "transition",
            transitionDirection: "next",
            transitionTargetChapter: (chapter ?? 1) + 1,
            transitionLabelEn: nextChapterLabelEn,
          },
        ]
      : [];
    const full = [...combined, ...tail];
    if (full.length % 2 !== 0) full.push({ variant: "blank" });
    return full;
  }, [tocPages, textPages, hasNext, chapter, nextChapterLabelEn]);

  // LTR: rendered order === semantic order, startPage = 0.
  const renderedPages = useMemo(() => [...pages], [pages]);
  const startPage = 0;

  useEffect(() => {
    pagingRef.current = { verses, tocPages, hasPrev, pages };
  }, [verses, tocPages, hasPrev, pages]);

  return (
    <div
      ref={stageRef}
      className="book-stage-3d"
      data-testid="book-reader-3d-en"
      dir="ltr"
      style={{ minHeight: `${dims.height + 120}px` }}
    >
      <div className="tanakh-book-wrapper" style={{ width: "auto" }}>
        <HTMLFlipBook
          ref={bookRef}
          width={dims.width}
          height={dims.height}
          size="fixed"
          minWidth={Math.min(180, dims.width)}
          maxWidth={Math.max(800, dims.width)}
          minHeight={Math.min(220, dims.height)}
          maxHeight={Math.max(1100, dims.height)}
          showCover={false}
          drawShadow={true}
          flippingTime={720}
          usePortrait={dims.portrait}
          startPage={startPage}
          mobileScrollSupport={true}
          maxShadowOpacity={0.6}
          className="tanakh-book"
          style={{}}
          startZIndex={0}
          autoSize={false}
          clickEventForward={true}
          useMouseEvents={true}
          swipeDistance={90}
          showPageCorners={true}
          disableFlipByClick={true}
          onFlip={(e) => {
            onPageFlip?.(e.data);
            if (!autoContinue) return;
            const idx = e.data;
            // Auto-continue at boundaries: tail page → next chapter,
            // first page → prev chapter.
            if (idx >= renderedPages.length - 1 && hasNext) {
              setTimeout(() => onNextChapter?.(), 800);
            } else if (idx <= 0 && hasPrev) {
              setTimeout(() => onPrevChapter?.(), 800);
            }
          }}
        >
          {renderedPages.map((p, i) => {
            const chapterLabelEn = chapterLabelEnFn
              ? chapterLabelEnFn(chapter)
              : undefined;
            const transitionTargetChapterLabelEn = chapterLabelEnFn
              ? chapterLabelEnFn(p.transitionTargetChapter)
              : undefined;
            return (
              <BookPageEn
                key={`${chapter}-${i}`}
                variant={p.variant || "text"}
                bookEn={bookEn}
                chapter={chapter}
                pageIndex={i}
                side={i % 2 === 0 ? "left" : "right"}
                verses={p.verses || []}
                startsChapter={!!p.startsChapter}
                activeVerseNum={activeVerseNum}
                onVerseClick={onVerseClick}
                highlights={highlights}
                notesByVerse={notesByVerse}
                showVerseNums={showVerseNums}
                tocContent={p.tocContent}
                chapterTitle={p.chapterTitle}
                transitionDirection={p.transitionDirection}
                onTransitionClick={
                  p.transitionDirection === "next" ? onNextChapter : onPrevChapter
                }
                transitionLabelEn={
                  p.transitionLabelEn ||
                  transitionTargetChapterLabelEn ||
                  (p.transitionDirection === "next" ? "Next Chapter" : "Previous Chapter")
                }
                transitionTargetChapter={p.transitionTargetChapter}
                chapterLabelEn={chapterLabelEn}
              />
            );
          })}
        </HTMLFlipBook>
      </div>
    </div>
  );
});

/**
 * Compute book stage dimensions. Lifted verbatim from BookReader3D so the
 * English book matches the Hebrew sibling perfectly (same dimensions on
 * every device class). Kept inline — copy by design, never imported,
 * so a future tweak to one doesn't accidentally drag the other.
 */
function computeDims(w, h) {
  let safeTop = 0;
  let safeBottom = 0;
  if (typeof window !== "undefined") {
    const cs = getComputedStyle(document.documentElement);
    safeTop = parseFloat(cs.getPropertyValue("--sat") || "0") || 0;
    safeBottom = parseFloat(cs.getPropertyValue("--sab") || "0") || 0;
  }
  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 1024;
  const isPhoneLandscape = !isMobile && h < 500;
  let chromeY;
  if (isMobile) chromeY = 44 + 36 + 72 + safeTop + safeBottom + 16;
  else if (isPhoneLandscape) chromeY = 48 + 56 + 24 + safeTop + safeBottom + 16;
  else chromeY = 64 + 64 + safeTop + safeBottom + 16;

  if (isMobile) {
    const pageW = Math.min(w - 16, 440);
    const idealH = Math.round(pageW * 1.38);
    const pageH = Math.max(360, Math.min(h - chromeY, idealH));
    return { width: pageW, height: pageH, portrait: true };
  }
  if (isPhoneLandscape) {
    const availW = w - 16;
    const pageW = Math.floor(availW / 2);
    // Cap at the visible viewport so the book never overflows in
    // phone-landscape (EN-only fix — HE keeps its scroll-vertically
    // behaviour). 480 stays the ideal target on tall phones; on short
    // ones we shrink to fit. 320 floor prevents the page becoming so
    // small the text becomes unreadable.
    const pageH = Math.max(320, Math.min(480, h - chromeY));
    return { width: pageW, height: pageH, portrait: false };
  }
  if (isTablet) {
    if (h > w) {
      const pageW = Math.min(w - 64, 560);
      const pageH = Math.min(h - chromeY, Math.round(pageW * 1.4));
      return { width: pageW, height: pageH, portrait: true };
    }
    const pageW = Math.min(Math.floor((w * 0.88 - 48) / 2), 560);
    const pageH = Math.min(h - chromeY, Math.round(pageW * 1.34));
    return { width: pageW, height: pageH, portrait: false };
  }
  const pageW = Math.min(Math.floor((w * 0.92 - 64) / 2), 680);
  const pageH = Math.min(h - chromeY, Math.round(pageW * 1.36));
  return { width: pageW, height: pageH, portrait: false };
}

/**
 * English page-builder. Slightly more generous per-page targets than
 * the Hebrew sibling (English is longer per verse). Verses without an
 * English translation are counted by their Hebrew length, so the page
 * never crams two long Hebrew-fallback verses where one English verse
 * normally fits.
 */
function buildTextPagesEn({ verses, maxCharsPerPage, maxVersesPerPage }) {
  if (!verses || verses.length === 0) return [{ variant: "blank" }];

  const pages = [];
  let current = [];
  let currentChars = 0;

  for (const v of verses) {
    const text = v.en && v.en.trim() ? v.en : (v.he || "");
    const vChars = text.length;
    if (
      current.length > 0 &&
      (current.length >= maxVersesPerPage ||
        currentChars + vChars > maxCharsPerPage)
    ) {
      pages.push({
        variant: "text",
        verses: current,
        startsChapter: pages.length === 0,
      });
      current = [];
      currentChars = 0;
    }
    current.push(v);
    currentChars += vChars;
  }
  if (current.length > 0) {
    pages.push({
      variant: "text",
      verses: current,
      startsChapter: pages.length === 0,
    });
  }
  return pages;
}

export default BookReader3DEn;
